import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { ArrowLeft, Trophy, Plus, X, Crown } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../App';
import LevelBadge, { ExpProgressBar } from '../components/LevelBadge';

const Tournament = ({ user, onBack }) => {
  const [tournaments, setTournaments] = useState([]);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [tournamentName, setTournamentName] = useState('');
  const [tournamentSize, setTournamentSize] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [selectedOrganizers, setSelectedOrganizers] = useState([]);
  const [showOrganizerModal, setShowOrganizerModal] = useState(false);
  const [organizerSearchQuery, setOrganizerSearchQuery] = useState('');
  const [organizerSearchResults, setOrganizerSearchResults] = useState([]);
  const [tournamentWs, setTournamentWs] = useState(null);

  useEffect(() => {
    fetchTournaments();
  }, []);

  // WebSocket connection for real-time tournament updates
  useEffect(() => {
    if (!selectedTournament) {
      // Disconnect WebSocket if no tournament selected
      if (tournamentWs) {
        console.log('🔌 Disconnecting from tournament WebSocket');
        tournamentWs.close();
        setTournamentWs(null);
      }
      return;
    }

    // Connect to tournament WebSocket
    const wsUrl = `${API.replace('https://', 'wss://').replace('http://', 'ws://').replace('/api', '')}/ws/tournament/${selectedTournament.id}`;
    console.log('🔌 Connecting to tournament WebSocket:', wsUrl);
    const websocket = new WebSocket(wsUrl);

    websocket.onopen = () => {
      console.log('✓ Tournament WebSocket connected');
    };

    websocket.onmessage = async (event) => {
      const data = JSON.parse(event.data);
      console.log('📨 Tournament WebSocket message:', data);
      
      // Handle tournament update
      if (data.type === 'tournament_update') {
        console.log('🔄 Tournament updated, reloading bracket...');
        // Reload tournament data to show latest bracket
        loadTournament(selectedTournament.id);
        // Show toast notification
        toast.success(data.message || 'Tournament bracket updated!');
      }
    };

    websocket.onerror = (error) => {
      console.error('❌ Tournament WebSocket error:', error);
    };

    websocket.onclose = () => {
      console.log('🔌 Tournament WebSocket disconnected');
    };

    setTournamentWs(websocket);

    // Cleanup on unmount or tournament change
    return () => {
      console.log('🔌 Cleaning up tournament WebSocket');
      websocket.close();
    };
  }, [selectedTournament?.id]);

  const fetchTournaments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/tournament/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTournaments(response.data.tournaments);
    } catch (error) {
      console.error('Error fetching tournaments:', error);
    }
  };

  const searchUsers = async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users/search?q=${query}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(response.data);
    } catch (error) {
      console.error('Error searching users:', error);
    }
  };



  const searchOrganizers = async (query) => {
    if (query.length < 2) {
      setOrganizerSearchResults([]);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users/search?q=${query}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Organizer search results:', response.data);
      setOrganizerSearchResults(response.data);
    } catch (error) {
      console.error('Error searching organizers:', error);
    }
  };

  const createTournament = async () => {
    const size = parseInt(tournamentSize);
    if (!tournamentName.trim() || !size || size < 1 || size > 100 || selectedParticipants.length !== size) {
      toast.error('Please enter a valid tournament size and select the correct number of participants');
      return;
    }

    try {
      // Shuffle/randomize participants for fair bracket seeding
      const shuffleArray = (array) => {
        const shuffled = [...array];
        for (let i = shuffled.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
      };
      
      const randomizedParticipants = shuffleArray(selectedParticipants);
      console.log('Participants randomized for bracket:', randomizedParticipants.map(p => p.username));

      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/tournament/create`,
        {
          name: tournamentName,
          size: size,
          participant_ids: randomizedParticipants.map(p => p.id),
          organizer_ids: selectedOrganizers.map(o => o.id),
          reward_points: 0
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Tournament created! Players randomized. ${selectedOrganizers.length} organizer(s) added.`);
      setShowCreateModal(false);
      setTournamentName('');
      setTournamentSize('');
      setSelectedParticipants([]);
      setSelectedOrganizers([]);
      setSearchQuery('');
      fetchTournaments();
    } catch (error) {
      console.error('Error creating tournament:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to create tournament. Please try again.');
      }
    }
  };

  const loadTournament = async (tournamentId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/tournament/${tournamentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedTournament(response.data);
    } catch (error) {
      console.error('Error loading tournament:', error);
    }
  };

  const reportWinner = async (matchId, winnerId) => {
    if (!selectedTournament) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/tournament/${selectedTournament.id}/match/${matchId}/winner`,
        { winner_id: winnerId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Winner reported!');
      loadTournament(selectedTournament.id);
      fetchTournaments();
    } catch (error) {
      // Error hidden - silent fail
    }
  };

  const deleteTournament = async (tournamentId) => {
    // Show confirmation dialog
    const confirmed = window.confirm('Delete this bracket?');
    if (!confirmed) {
      return; // User clicked "No" or cancelled
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/tournament/${tournamentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Tournament deleted');
      setSelectedTournament(null);
      fetchTournaments();
    } catch (error) {
      // Error hidden - silent fail
    }
  };

  const renderMatchCard = (match, cardWidth = 180) => {
    const renderPlayer = (playerData, isWinner) => {
      const { username, profile_picture, banner_image, circle_color, level, exp } = playerData || {};
      
      if (!username || username === 'TBD') {
        return (
          <div className={`p-2 rounded mb-1 flex items-center gap-2 ${isWinner ? 'bg-cyan-500/20 border border-cyan-500' : 'bg-black'}`}>
            <div className="w-6 h-6 rounded-full bg-black flex items-center justify-center text-xs text-slate-400">
              ?
            </div>
            <div className="flex-1 min-w-0">
              <div className="bg-black rounded px-2 py-1">
                <span className="text-white text-xs">TBD</span>
              </div>
            </div>
            {isWinner && <Crown className="w-3 h-3 text-yellow-400 flex-shrink-0" />}
          </div>
        );
      }

      const { battle_points } = playerData || {};
      
      return (
        <div 
          className={`leaderboard-entry py-3 px-2 pb-3.5 rounded mb-1 flex items-center relative min-w-[155px] min-h-[48px] ${isWinner ? 'bg-cyan-500/20 border-2 border-cyan-500' : 'bg-slate-800/30 border border-slate-700/50'}`}
          style={banner_image ? {
            backgroundImage: `url(${banner_image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          } : {}}
        >
          {/* Overlay for better text visibility when banner is set */}
          {banner_image && (
            <div className="absolute inset-0 bg-black/40 rounded"></div>
          )}
          
          {/* Level Badge - Top Left */}
          <div className="absolute top-0 left-1 text-[8px] text-white font-bold digital-font drop-shadow-lg z-10">
            Lv {level || 1}
          </div>
          
          {/* Battle Points - Top Right */}
          <div className="absolute top-0 right-1 z-10 flex items-center gap-0.5">
            <span className={`text-[8px] digital-font font-semibold ${banner_image ? 'text-white drop-shadow-lg' : 'text-cyan-400'}`}>
              {battle_points || 0} BP
            </span>
            {isWinner && <Crown className="w-3 h-3 text-yellow-400 flex-shrink-0" />}
          </div>
          
          {/* Center: Profile Picture + Username */}
          <div className="flex items-center gap-2 relative z-10 w-full justify-center">
            {/* Profile Picture */}
            {profile_picture ? (
              <img
                src={profile_picture}
                alt={username}
                className="w-5 h-5 rounded-full object-cover border flex-shrink-0"
                style={{ borderColor: circle_color || '#475569' }}
              />
            ) : (
              <div 
                className="w-5 h-5 rounded-full bg-slate-700 border flex items-center justify-center text-slate-300 font-semibold text-[8px] flex-shrink-0"
                style={{ borderColor: circle_color || '#475569' }}
              >
                {username.charAt(0).toUpperCase()}
              </div>
            )}
            
            {/* Username */}
            <span className={`text-xs font-medium truncate ${banner_image ? 'text-white drop-shadow-lg' : 'text-slate-200'}`}>
              {username}
            </span>
          </div>
          
          {/* Exp Label - Bottom Left Corner */}
          <span className="absolute bottom-0 left-1 text-[8px] text-slate-400 font-semibold" style={{ zIndex: 10 }}>
            Exp
          </span>
          
          {/* EXP Progress Bar - Bottom (Centered, smaller) */}
          <div className="absolute bottom-0 h-1.5 bg-slate-900/50 rounded-full overflow-hidden z-10" style={{ left: '12%', right: '12%' }}>
            <div 
              className="h-full bg-gradient-to-r from-blue-500 to-cyan-400 transition-all duration-500 ease-out rounded-full"
              style={{ width: `${level >= 100 ? 100 : Math.min(((exp || 0) / ((level || 1) * 100)) * 100, 100)}%` }}
            />
          </div>
        </div>
      );
    };

    // Get player data from match
    const player1Data = {
      username: match.player1_username,
      profile_picture: match.player1_profile_picture,
      banner_image: match.player1_banner_image,
      circle_color: match.player1_circle_color,
      battle_points: match.player1_battle_points,
      level: match.player1_level,
      exp: match.player1_exp
    };
    
    const player2Data = {
      username: match.player2_username,
      profile_picture: match.player2_profile_picture,
      banner_image: match.player2_banner_image,
      circle_color: match.player2_circle_color,
      battle_points: match.player2_battle_points,
      level: match.player2_level,
      exp: match.player2_exp
    };

    // Use tournament creator's device frame for entire match card
    const deviceFrame = selectedTournament?.creator_device_frame;

    return (
      <div
        key={match.match_id}
        className="rounded-lg p-2.5 relative overflow-hidden"
        style={deviceFrame ? {
          width: `${cardWidth}px`,
          minHeight: '130px',
          backgroundImage: `url(${deviceFrame})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        } : { 
          width: `${cardWidth}px`,
          minHeight: '130px',
          backgroundColor: '#1e293b'
        }}
      >
        {/* Overlay for better content visibility when device frame is set */}
        {deviceFrame && (
          <div className="absolute inset-0 bg-black/40 rounded-lg"></div>
        )}
        
        <div className="relative z-10 flex flex-col items-center">
          {/* Player 1 */}
          {renderPlayer(player1Data, match.winner_id === match.player1_id)}

          {/* VS */}
          <div className="text-center text-slate-500 text-xs my-1">VS</div>

          {/* Player 2 */}
          {renderPlayer(player2Data, match.winner_id === match.player2_id)}

          {/* Report Winner Buttons */}
          {!match.winner_id && match.player1_id && match.player2_id && 
           (selectedTournament.creator_id === user.id || selectedTournament.organizers?.includes(user.id)) && 
           selectedTournament.status !== 'completed' && (
            <div className="flex gap-1 mt-2">
              <Button
                onClick={() => reportWinner(match.match_id, match.player1_id)}
                size="sm"
                className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-[10px] py-1 h-auto"
              >
                P1
              </Button>
              <Button
                onClick={() => reportWinner(match.match_id, match.player2_id)}
                size="sm"
                className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-[10px] py-1 h-auto"
              >
                P2
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderBracket = () => {
    if (!selectedTournament) return null;

    const { matches } = selectedTournament;
    const rounds = {};

    // Group matches by round
    matches.forEach(match => {
      if (!rounds[match.round]) {
        rounds[match.round] = [];
      }
      rounds[match.round].push(match);
    });

    const totalRounds = Object.keys(rounds).length;
    const roundNumbers = Object.keys(rounds).sort((a, b) => Number(a) - Number(b)); // Forward order for pyramid (bottom to top)
    
    // Calculate optimal card size based on the maximum number of matches in any round
    const maxMatchesInRound = Math.max(...Object.values(rounds).map(r => r.length));
    const cardWidth = Math.max(160, Math.min(200, (window.innerWidth * 0.38) / maxMatchesInRound - 16));
    
    return (
      <div className="p-4">
        <div className="flex flex-col-reverse gap-6 items-center w-full min-h-[400px]">
          {roundNumbers.map((roundNum, roundIndex) => {
            const matchesInRound = rounds[roundNum];
            const isFinal = roundNum == totalRounds;
            const isFirstRound = roundNum == 1;
            
            // Calculate pyramid positioning
            const pyramidLevel = totalRounds - roundIndex;
            const levelWidth = Math.pow(2, pyramidLevel - 1);
            const containerWidth = levelWidth * (cardWidth + 16);
            
            return (
              <div key={roundNum} className="flex flex-col items-center relative w-full">
                {/* Round Label */}
                <h3 className="text-cyan-400 font-semibold mb-3 text-center text-sm">
                  {isFinal ? '🏆 FINAL' : isFirstRound ? '📍 FIRST ROUND' : `Round ${roundNum}`}
                </h3>
                
                {/* Matches in pyramid formation - display in pairs */}
                <div className="flex flex-col items-center gap-6 w-full">
                  {isFinal ? (
                    // Final match - single centered card
                    <div className="flex justify-center">
                      {renderMatchCard(matchesInRound[0], cardWidth)}
                    </div>
                  ) : (
                    // Other rounds - display in pairs (2 cards per row)
                    Array.from({ length: Math.ceil(matchesInRound.length / 2) }, (_, pairIndex) => {
                      const match1 = matchesInRound[pairIndex * 2];
                      const match2 = matchesInRound[pairIndex * 2 + 1];
                      
                      return (
                        <div key={pairIndex} className="flex justify-center items-center gap-4">
                          {match1 && renderMatchCard(match1, cardWidth)}
                          {match2 && renderMatchCard(match2, cardWidth)}
                        </div>
                      );
                    })
                  )}
                </div>
                
                {/* Connection lines for pyramid structure */}
                {!isFinal && (
                  <div className="w-full flex justify-center items-center mt-4 mb-2">
                    <div className="flex gap-8">
                      {Array.from({ length: Math.ceil(matchesInRound.length / 2) }, (_, i) => (
                        <div key={i} className="flex flex-col items-center">
                          <div className="w-px h-6 bg-cyan-500/30"></div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen relative">
      {/* Fixed Background Layer */}
      <div 
        className="fixed inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950"
        style={{ 
          backgroundImage: 'url(/bracket-background.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed'
        }}
      >
        {/* Dark overlay for better text visibility */}
        <div className="absolute inset-0 bg-black/50"></div>
      </div>

      {/* Scrollable Content Layer */}
      <div className="relative z-10 p-4" style={{ paddingTop: 'calc(env(safe-area-inset-top) + 2.5rem)' }}>
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <button
              onClick={onBack}
              className="text-cyan-400 hover:text-cyan-300 transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-2xl font-bold text-cyan-400 digital-font flex items-center gap-2">
              <Trophy className="w-6 h-6" />
              TOURNAMENTS
            </h1>
          </div>

          {/* Create Bracket Button */}
          <div className="flex justify-center mb-6">
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-cyan-500 hover:bg-cyan-600"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Bracket
            </Button>
          </div>

          {selectedTournament ? (
            /* Tournament Bracket View */
            <div className="flex flex-col h-[calc(100vh-12rem)]">
              {/* Fixed Header */}
              <div 
                className="rounded-lg border border-slate-700 p-4 mb-4 relative overflow-hidden flex-shrink-0"
                style={selectedTournament.creator_banner ? {
                  backgroundImage: `url(${selectedTournament.creator_banner})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat'
                } : {
                  backgroundColor: 'rgba(30, 41, 59, 0.5)'
                }}
              >
                {/* Overlay for better text visibility when banner is set */}
                {selectedTournament.creator_banner && (
                  <div className="absolute inset-0 bg-black/50 rounded-lg"></div>
                )}
                
                {/* Buttons - Absolute positioned in top-right */}
                <div className="absolute top-2 right-2 flex gap-1 z-20">
                  <Button
                    onClick={() => setSelectedTournament(null)}
                    variant="outline"
                    size="sm"
                    className="border-slate-700 bg-slate-900/90 text-slate-300 text-[10px] px-2 py-1 h-auto hover:bg-slate-800/90"
                  >
                    Back
                  </Button>
                  {selectedTournament.creator_id === user.id && (
                    <Button
                      onClick={() => deleteTournament(selectedTournament.id)}
                      variant="outline"
                      size="sm"
                      className="border-red-500 bg-slate-900/90 text-red-400 hover:bg-red-500/20 text-[10px] px-2 py-1 h-auto"
                    >
                      Delete
                    </Button>
                  )}
                </div>

                {/* Tournament Info - Full width, buttons won't affect layout */}
                <div className="relative z-10 pr-24">
                  <h2 className={`text-xl font-bold ${selectedTournament.creator_banner ? 'text-white drop-shadow-lg' : 'text-white'}`}>
                    {selectedTournament.name}
                  </h2>
                  <p className={`text-sm ${selectedTournament.creator_banner ? 'text-white drop-shadow-md' : 'text-slate-400'}`}>
                    Created by {selectedTournament.creator_username} • {selectedTournament.size} players
                    {selectedTournament.reward_points > 0 && ` • ${selectedTournament.reward_points} BP reward`}
                  </p>
                  {selectedTournament.organizer_profiles && selectedTournament.organizer_profiles.length > 0 && (
                    <p className={`text-sm mt-1 ${selectedTournament.creator_banner ? 'text-white/90 drop-shadow-md' : 'text-slate-400'}`}>
                      Organizers/Judges: {selectedTournament.organizer_profiles.map(org => org.username).join(', ')}
                    </p>
                  )}
                  {selectedTournament.status === 'completed' && (
                    <p className={`font-semibold mt-2 flex items-center gap-2 ${selectedTournament.creator_banner ? 'text-white drop-shadow-lg' : 'text-cyan-400'}`}>
                      <Crown className="w-5 h-5 text-yellow-400" />
                      Winner: {selectedTournament.winner_username}
                    </p>
                  )}
                </div>
              </div>

              {/* Scrollable Bracket Area */}
              <div className="flex-1 overflow-y-auto overflow-x-hidden">
                {renderBracket()}
              </div>
            </div>
          ) : (
            /* Tournament List */
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {tournaments.map((tournament) => (
                <button
                  key={tournament.id}
                  onClick={() => loadTournament(tournament.id)}
                  className="border border-slate-700 rounded-lg p-4 hover:bg-slate-800/70 transition-colors text-left relative overflow-hidden"
                  style={tournament.creator_banner ? {
                    backgroundImage: `url(${tournament.creator_banner})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                  } : {
                    backgroundColor: 'rgba(30, 41, 59, 0.5)'
                  }}
                >
                  {/* Overlay for better text visibility when banner is set */}
                  {tournament.creator_banner && (
                    <div className="absolute inset-0 bg-black/50 rounded-lg"></div>
                  )}
                  
                  <div className="relative z-10">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className={`text-lg font-semibold ${tournament.creator_banner ? 'text-white drop-shadow-lg' : 'text-white'}`}>
                        {tournament.name}
                      </h3>
                      {tournament.status === 'completed' && (
                        <Crown className="w-5 h-5 text-yellow-400" />
                      )}
                    </div>
                    <p className={`text-sm mb-2 ${tournament.creator_banner ? 'text-white drop-shadow-md' : 'text-slate-400'}`}>
                      By {tournament.creator_username}
                    </p>
                    <div className="flex items-center gap-4 text-sm">
                      <span className={tournament.creator_banner ? 'text-white drop-shadow-md' : 'text-slate-400'}>
                        {tournament.size} players
                      </span>
                      {tournament.reward_points > 0 && (
                        <span className={tournament.creator_banner ? 'text-cyan-300 drop-shadow-md font-semibold' : 'text-cyan-400'}>
                          {tournament.reward_points} BP
                        </span>
                      )}
                      <span className={`px-2 py-1 rounded text-xs ${
                        tournament.status === 'completed' 
                          ? 'bg-green-500/30 text-green-300 border border-green-500/50'
                          : 'bg-cyan-500/30 text-cyan-300 border border-cyan-500/50'
                      }`}>
                        {tournament.status}
                      </span>
                    </div>
                    {tournament.status === 'completed' && (
                      <p className={`text-sm mt-2 flex items-center gap-1 ${tournament.creator_banner ? 'text-yellow-300 drop-shadow-lg font-semibold' : 'text-yellow-400'}`}>
                        <Crown className="w-4 h-4" />
                        {tournament.winner_username}
                      </p>
                    )}
                  </div>
                </button>
              ))}

              {tournaments.length === 0 && (
                <div className="col-span-full text-center text-slate-500 py-12">
                  No tournaments yet. Create one to get started!
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create Tournament Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl max-h-[95vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-cyan-400">Create Bracket</DialogTitle>
            <DialogDescription className="text-slate-400">
              Set up a single-elimination bracket tournament
            </DialogDescription>
          </DialogHeader>
          
          {/* Create Button at Top */}
          <Button
            onClick={createTournament}
            disabled={!tournamentName.trim() || !tournamentSize || selectedParticipants.length !== parseInt(tournamentSize)}
            className="bg-cyan-500 hover:bg-cyan-600 w-full text-lg font-semibold flex-shrink-0"
          >
            Finalize
          </Button>
          
          <div className="space-y-4 overflow-y-auto flex-1 min-h-0">
            <div>
              <label className="text-sm text-slate-400 mb-2 block">Tournament Name</label>
              <Input
                value={tournamentName}
                onChange={(e) => setTournamentName(e.target.value)}
                placeholder="League"
                className="bg-slate-800 border-slate-700 text-white"
                maxLength={50}
              />
            </div>

            <div>
              <label className="text-sm text-slate-400 mb-2 block">Tournament Size (4-100 players)</label>
              <Input
                type="number"
                value={tournamentSize}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow free typing - just set the value as-is
                  setTournamentSize(value);
                  setSelectedParticipants([]);
                }}
                onBlur={(e) => {
                  // Validate only when user leaves the field
                  const value = e.target.value;
                  if (value === '') {
                    return;
                  }
                  const num = parseInt(value);
                  if (!isNaN(num)) {
                    const size = Math.max(4, Math.min(100, num));
                    setTournamentSize(size);
                  }
                }}
                placeholder="Enter number of players"
                className="bg-slate-800 border-slate-700 text-white"
                min={4}
                max={100}
              />
              <p className="text-xs text-slate-500 mt-1">
                Enter any number from 4-100 for single elimination bracket.
              </p>
            </div>

            <div>
              <label className="text-sm text-slate-400 mb-2 block">
                Select Participants ({selectedParticipants.length}/{tournamentSize})
              </label>
              <Input
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  searchUsers(e.target.value);
                }}
                placeholder="Search users..."
                className="bg-slate-800 border-slate-700 text-white mb-2"
              />

              {/* Participant Count */}
              {tournamentSize && (
                <div className="text-sm text-slate-400 mb-2">
                  Selected: {selectedParticipants.length} / {tournamentSize} players
                </div>
              )}

              {/* Selected Participants */}
              {selectedParticipants.length > 0 && (
                <div className="max-h-80 overflow-y-auto mb-3 space-y-2 pr-2">
                  {selectedParticipants.map((participant) => (
                    <div
                      key={participant.username}
                      className="leaderboard-entry bg-cyan-500/20 border border-cyan-500 rounded-lg p-3 flex justify-between items-center relative"
                      style={participant.banner_image ? {
                        backgroundImage: `url(${participant.banner_image})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat'
                      } : {}}
                    >
                      {/* Overlay for better text visibility when banner is set */}
                      {participant.banner_image && (
                        <div className="absolute inset-0 bg-black/40 rounded-lg"></div>
                      )}
                      
                      <div className="flex items-center gap-3 relative z-10">
                        {/* Profile Picture */}
                        {participant.profile_picture ? (
                          <img
                            src={participant.profile_picture}
                            alt={participant.username}
                            className="w-12 h-12 rounded-full object-cover border-2 flex-shrink-0"
                            style={{ borderColor: participant.circle_color || '#475569' }}
                          />
                        ) : (
                          <div 
                            className="w-12 h-12 rounded-full bg-slate-700 border-2 flex items-center justify-center text-slate-400 font-semibold text-sm flex-shrink-0"
                            style={{ borderColor: participant.circle_color || '#475569' }}
                          >
                            {participant.username.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <span className={`font-medium ${participant.banner_image ? 'text-white drop-shadow-lg' : 'text-cyan-400'}`}>
                          {participant.username}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-2 relative z-10">
                        <span className={`digital-font font-semibold ${participant.banner_image ? 'text-white drop-shadow-lg' : 'text-cyan-400'}`}>
                          {participant.battle_points} BP
                        </span>
                        <button
                          onClick={() => setSelectedParticipants(prev => prev.filter(p => p.username !== participant.username))}
                          className="text-cyan-400 hover:text-cyan-300 flex-shrink-0"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Search Results */}
              <div className="max-h-48 overflow-y-auto space-y-2">
                {searchResults
                  .filter(r => !selectedParticipants.find(p => p.username === r.username))
                  .map((result) => (
                    <button
                      key={result.username}
                      onClick={() => {
                        if (selectedParticipants.length < tournamentSize) {
                          setSelectedParticipants(prev => [...prev, result]);
                        }
                      }}
                      disabled={selectedParticipants.length >= tournamentSize}
                      className="w-full leaderboard-entry bg-slate-800/30 border border-slate-700/50 rounded-lg p-3 hover:bg-slate-700 transition-colors disabled:opacity-50 flex justify-between items-center relative"
                      style={result.banner_image ? {
                        backgroundImage: `url(${result.banner_image})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat'
                      } : {}}
                    >
                      {/* Overlay for better text visibility when banner is set */}
                      {result.banner_image && (
                        <div className="absolute inset-0 bg-black/40 rounded-lg"></div>
                      )}
                      
                      <div className="flex items-center gap-3 relative z-10">
                        {/* Profile Picture */}
                        {result.profile_picture ? (
                          <img
                            src={result.profile_picture}
                            alt={result.username}
                            className="w-12 h-12 rounded-full object-cover border-2 flex-shrink-0"
                            style={{ borderColor: result.circle_color || '#475569' }}
                          />
                        ) : (
                          <div 
                            className="w-12 h-12 rounded-full bg-slate-700 border-2 flex items-center justify-center text-slate-400 font-semibold text-sm flex-shrink-0"
                            style={{ borderColor: result.circle_color || '#475569' }}
                          >
                            {result.username.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <span className={`font-medium ${result.banner_image ? 'text-white drop-shadow-lg' : 'text-slate-200'}`}>
                          {result.username}
                        </span>
                      </div>
                      
                      <span className={`digital-font font-semibold relative z-10 ${result.banner_image ? 'text-white drop-shadow-lg' : 'text-cyan-400'}`}>
                        {result.battle_points} BP
                      </span>
                    </button>
                  ))}
              </div>
            </div>

            {/* Organizers Section */}
            <div>
              <label className="text-sm text-slate-400 mb-2 block">
                Select Organizers/Judges (Optional) - Can also select bracket winners
              </label>
              <p className="text-xs text-slate-500 mb-2">
                Organizers/Judges can report match winners alongside you
              </p>
              <Button
                onClick={() => setShowOrganizerModal(true)}
                variant="outline"
                className="w-full border-slate-700 text-slate-300 mb-2"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Organizers ({selectedOrganizers.length})
              </Button>

              {/* Selected Organizers */}
              {selectedOrganizers.length > 0 && (
                <div className="space-y-2">
                  {selectedOrganizers.map((organizer) => (
                    <div
                      key={organizer.username}
                      className="bg-purple-500/20 border border-purple-500 rounded-lg p-3 flex justify-between items-center relative overflow-hidden"
                      style={organizer.banner_image ? {
                        backgroundImage: `url(${organizer.banner_image})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat'
                      } : {}}
                    >
                      {/* Overlay for better text visibility when banner is set */}
                      {organizer.banner_image && (
                        <div className="absolute inset-0 bg-black/40 rounded-lg"></div>
                      )}
                      
                      <div className="flex items-center gap-3 relative z-10">
                        {/* Profile Picture */}
                        {organizer.profile_picture ? (
                          <img
                            src={organizer.profile_picture}
                            alt={organizer.username}
                            className="w-12 h-12 rounded-full object-cover border-2 flex-shrink-0"
                            style={{ borderColor: organizer.circle_color || '#475569' }}
                          />
                        ) : (
                          <div 
                            className="w-12 h-12 rounded-full bg-slate-700 border-2 flex items-center justify-center text-slate-400 font-semibold text-sm flex-shrink-0"
                            style={{ borderColor: organizer.circle_color || '#475569' }}
                          >
                            {organizer.username.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <span className={`font-medium ${organizer.banner_image ? 'text-white drop-shadow-lg' : 'text-purple-400'}`}>
                          {organizer.username}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-2 relative z-10">
                        <span className={`digital-font font-semibold ${organizer.banner_image ? 'text-white drop-shadow-lg' : 'text-purple-400'}`}>
                          {organizer.battle_points} BP
                        </span>
                        <button
                          onClick={() => setSelectedOrganizers(prev => prev.filter(o => o.username !== organizer.username))}
                          className="text-purple-400 hover:text-purple-300 flex-shrink-0"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Organizer Selection Modal */}
      <Dialog open={showOrganizerModal} onOpenChange={setShowOrganizerModal}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-purple-400">Add Organizers</DialogTitle>
            <DialogDescription className="text-slate-400">
              Search and add users who can help report match winners
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <Input
              value={organizerSearchQuery}
              onChange={(e) => {
                setOrganizerSearchQuery(e.target.value);
                searchOrganizers(e.target.value);
              }}
              placeholder="Search users..."
              className="bg-slate-800 border-slate-700 text-white"
            />

            {/* Search Results */}
            <div className="max-h-60 overflow-y-auto space-y-2">
              {organizerSearchResults
                .filter(r => !selectedOrganizers.find(o => o.username === r.username))
                .filter(r => r.username !== user.username)
                .map((result) => (
                  <button
                    key={result.username}
                    onClick={() => {
                      setSelectedOrganizers(prev => [...prev, result]);
                      setOrganizerSearchQuery('');
                      setOrganizerSearchResults([]);
                    }}
                    className="w-full bg-slate-800 hover:bg-slate-700 rounded-lg p-3 flex justify-between items-center transition-colors text-left relative overflow-hidden"
                    style={result.banner_image ? {
                      backgroundImage: `url(${result.banner_image})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat'
                    } : {}}
                  >
                    {/* Overlay for better text visibility when banner is set */}
                    {result.banner_image && (
                      <div className="absolute inset-0 bg-black/40 rounded-lg"></div>
                    )}
                    
                    <div className="flex items-center gap-3 relative z-10">
                      {/* Profile Picture */}
                      {result.profile_picture ? (
                        <img
                          src={result.profile_picture}
                          alt={result.username}
                          className="w-12 h-12 rounded-full object-cover border-2 flex-shrink-0"
                          style={{ borderColor: result.circle_color || '#475569' }}
                        />
                      ) : (
                        <div 
                          className="w-12 h-12 rounded-full bg-slate-700 border-2 flex items-center justify-center text-slate-400 font-semibold text-sm flex-shrink-0"
                          style={{ borderColor: result.circle_color || '#475569' }}
                        >
                          {result.username.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <span className={`font-medium ${result.banner_image ? 'text-white drop-shadow-lg' : 'text-slate-200'}`}>
                        {result.username}
                      </span>
                    </div>
                    
                    <span className={`digital-font font-semibold relative z-10 ${result.banner_image ? 'text-white drop-shadow-lg' : 'text-purple-400'}`}>
                      {result.battle_points} BP
                    </span>
                  </button>
                ))}
            </div>
          </div>

          <DialogFooter>
            <Button
              onClick={() => setShowOrganizerModal(false)}
              className="bg-purple-500 hover:bg-purple-600 w-full"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Tournament;
